import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { DashboardT, SharingPayloadT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'routes/AllDashboards/hooks/getDashboardsList';
import { FAVOURITE_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/favouriteDashboards';
import { LAST_VIEWED_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/useGetLastViewedDashboards';
import { RQAPICallWithData } from 'store/APIClient';

const shareDashboard = async (sharing: SharingPayloadT, key: number) => {
    const response = await RQAPICallWithData<DashboardT>({
        endpoint: `explore/dashboard/${key}/share`,
        body: sharing,
        method: 'POST',
    });
    return response.content;
};

/**
 * RQ Mutation to share a dashboard.
 * The mutation takes as a parameter a key, that is the id of the dashboard to update,
 * and an object indicating the sharing properties and the users to share to.
 */
export const useShareDashboard = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            sharing,
            key,
        }: {
            sharing: SharingPayloadT;
            key: number;
        }) => shareDashboard(sharing, key),

        onSuccess: ({ data: sharedDashboard, message }) => {
            trackEvent(
                'decision/dashboard/options/share-modal/share-confirm-click',
            );
            const setQueryForDashboard = (queryKey: string) =>
                queryClient.setQueryData(
                    [queryKey],
                    (oldDashboards: DashboardT[] | undefined) =>
                        oldDashboards?.map((dashboard) =>
                            dashboard.key === sharedDashboard.key
                                ? sharedDashboard
                                : dashboard,
                        ),
                );
            setQueryForDashboard(ACTIVE_DASHBOARDS_QUERY_KEY);
            setQueryForDashboard(FAVOURITE_DASHBOARDS_QUERY_KEY);
            setQueryForDashboard(LAST_VIEWED_DASHBOARDS_QUERY_KEY);
            queryClient.setQueryData(
                ['getDashboard', sharedDashboard.slug, ''],
                (oldDashboard: DashboardT) => {
                    if (oldDashboard?.key === sharedDashboard.key) {
                        return {
                            ...oldDashboard,
                            usersSharing: sharedDashboard.usersSharing,
                            groupsSharing: sharedDashboard.groupsSharing,
                            orgSharing: sharedDashboard.orgSharing,
                        };
                    }
                    return oldDashboard;
                },
            );
            dispatch.vision.notifyUser({ message });
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};
